/* eslint-disable react/display-name */
import {
	ChangeEvent,
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { BsClipboard2CheckFill, BsLockFill } from 'react-icons/bs';

import { RegulatoryTrainingMatrix } from '@/types/Training';

import Check from '../../assets/icons/check.svg';
import Checkbox from '../atoms/Checkbox';
import LightTooltip from '../atoms/LightTooltip';

interface RegulatoryTrainingMatrixCellProps {
	isEditMode: boolean;
	rowIndex: number;
	colIndex: number;
	positionId: number;
	trainingId: number;
	totalWorkload: number | null;
	validity: number | null;
	recyclingWorkload: number | null;
	trainingsMatrixData?: RegulatoryTrainingMatrix;
	modifiedCheckboxes: Map<string, boolean>;
	canSave?: boolean;
	isResponsible?: boolean;
	onChangeCallback: (checked: boolean) => void;
	positionGap: number;
	modifiedBlockCheckboxes: Map<string, boolean>;
	onBlockChangeCallback: (checked: boolean) => void;
	canBlockTraining: boolean | undefined;
	canMapRegulatoryTraining: boolean | undefined;
}

const RegulatoryTrainingMatrixCell = memo(
	({
		isEditMode,
		rowIndex,
		colIndex,
		trainingId,
		totalWorkload,
		validity,
		recyclingWorkload,
		positionId,
		trainingsMatrixData,
		modifiedCheckboxes,
		canSave,
		isResponsible,
		onChangeCallback,
		positionGap,
		modifiedBlockCheckboxes,
		onBlockChangeCallback,
		canBlockTraining,
		canMapRegulatoryTraining,
	}: RegulatoryTrainingMatrixCellProps) => {
		const checkboxRef = useRef(null);
		const blockCheckboxRef = useRef(null);
		const positionIndex = rowIndex - positionGap;
		const trainingIndex = colIndex;

		// Create a key to look up values
		const cellKey = `${positionId}-${trainingId}`;

		// Get checkbox states from matrices or modified maps
		const getCheckedState = useCallback(() => {
			if (modifiedCheckboxes.has(cellKey)) {
				return modifiedCheckboxes.get(cellKey) || false;
			} else if (
				trainingsMatrixData?.matrix &&
				trainingsMatrixData?.matrix.length > positionIndex &&
				positionIndex >= 0 &&
				trainingsMatrixData?.matrix[positionIndex]?.length > trainingIndex &&
				trainingIndex >= 0
			) {
				return trainingsMatrixData?.matrix[positionIndex][trainingIndex];
			}
			return false;
		}, [
			cellKey,
			modifiedCheckboxes,
			positionIndex,
			trainingIndex,
			trainingsMatrixData?.matrix,
		]);

		const getBlockCheckedState = useCallback(() => {
			if (modifiedBlockCheckboxes.has(cellKey)) {
				return modifiedBlockCheckboxes.get(cellKey) || false;
			} else if (
				trainingsMatrixData?.blockMatrix &&
				trainingsMatrixData?.blockMatrix.length > positionIndex &&
				positionIndex >= 0 &&
				trainingsMatrixData?.blockMatrix[positionIndex]?.length >
					trainingIndex &&
				trainingIndex >= 0
			) {
				return trainingsMatrixData?.blockMatrix[positionIndex][trainingIndex];
			}
			return false;
		}, [
			cellKey,
			modifiedBlockCheckboxes,
			positionIndex,
			trainingIndex,
			trainingsMatrixData?.blockMatrix,
		]);

		// Calculate initial states once
		const [isChecked, setIsChecked] = useState(() => getCheckedState());
		const [isBlockChecked, setIsBlockChecked] = useState(() =>
			getBlockCheckedState(),
		);

		// Only update states when dependencies change
		useEffect(() => {
			const newCheckedState = getCheckedState();
			const newBlockCheckedState = getBlockCheckedState();

			// Only update state if values have changed to minimize re-renders
			if (canMapRegulatoryTraining) {
				if (isChecked !== newCheckedState) {
					setIsChecked(newCheckedState);
				}
			}

			// Only update block state if user can block
			if (canBlockTraining) {
				if (isBlockChecked !== newBlockCheckedState) {
					setIsBlockChecked(newBlockCheckedState);
				}
			}
		}, [
			getCheckedState,
			getBlockCheckedState,
			isChecked,
			isBlockChecked,
			canBlockTraining,
			canMapRegulatoryTraining,
		]);

		const handleCheckboxChange = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				if (!canMapRegulatoryTraining) return;
				const checked = event.target?.checked;
				if (!checked && isBlockChecked) {
					return;
				}
				onChangeCallback(checked);
			},
			[isBlockChecked, onChangeCallback, canMapRegulatoryTraining],
		);

		const handleBlockCheckboxChange = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				if (!canBlockTraining) return;
				const checked = event.target?.checked;
				if (checked && !isChecked) {
					return;
				}
				onBlockChangeCallback(checked);
			},
			[isChecked, onBlockChangeCallback, canBlockTraining],
		);

		// Special rendering for header rows
		if (rowIndex === 0) {
			return (
				<div className="flex items-center justify-center px-2 text-[13px] font-bold text-blue-800">
					<div>{`${validity || ''}`}</div>
				</div>
			);
		} else if (rowIndex === 1) {
			return (
				<div className="flex items-center justify-center px-2 text-[13px] font-bold text-blue-800">
					<div>{`${totalWorkload || ''}`}</div>
				</div>
			);
		} else if (rowIndex === 2) {
			return (
				<div className="flex items-center justify-center px-2 text-[13px] font-bold text-blue-800">
					<div>{`${recyclingWorkload || ''}`}</div>
				</div>
			);
		} else if (rowIndex === 3) {
			return <div></div>;
		}

		// Normal cell rendering
		return (
			<div className="flex items-center justify-center gap-4">
				{isEditMode ? (
					<div className="flex items-center gap-4">
						<div className="flex min-w-[64px] items-center justify-center gap-2">
							<Checkbox
								className={`${isBlockChecked ? 'cursor-not-allowed opacity-50' : ''}`}
								checked={isChecked}
								ref={checkboxRef}
								onChange={handleCheckboxChange}
								disabled={
									!canMapRegulatoryTraining ||
									(!canSave && !isResponsible && isBlockChecked)
								}
								size="large"
								variant="blue"
							/>
							<BsClipboard2CheckFill size={18} color="#00d692" />
						</div>

						<div className="flex min-w-[64px] items-center justify-center gap-2">
							<Checkbox
								className={`${!isChecked || !canBlockTraining ? 'cursor-not-allowed opacity-50' : ''}`}
								checked={isBlockChecked}
								ref={blockCheckboxRef}
								onChange={handleBlockCheckboxChange}
								disabled={
									!canBlockTraining ||
									(!canSave && !isResponsible && !isChecked)
								}
								size="large"
								variant="blue"
							/>
							<LightTooltip
								title="Sem este treinamento, o acesso pela catraca será bloqueado."
								arrow
							>
								<div>
									<BsLockFill size={18} color="#f4503a" />
								</div>
							</LightTooltip>
						</div>
					</div>
				) : (
					<div className="flex items-center gap-4">
						<div className="flex min-w-[64px] items-center justify-center gap-2">
							{isChecked && (
								<>
									<img src={Check} className="size-6 flex-none" />
									<BsClipboard2CheckFill size={18} color="#00d692" />
								</>
							)}
						</div>

						<div className="flex min-w-[64px] items-center justify-center gap-2">
							{isBlockChecked && (
								<>
									<img src={Check} className="size-6 flex-none" />
									<BsLockFill size={18} color="#f4503a" />
								</>
							)}
						</div>
					</div>
				)}
			</div>
		);
	},
);

// Add display name for better debugging
RegulatoryTrainingMatrixCell.displayName = 'RegulatoryTrainingMatrixCell';

export default RegulatoryTrainingMatrixCell;
